import { AppSettingType, Model, Status } from "@api/apiClient";
import { useAppSettings } from "@api/appSettings";
import { useProject } from "@api/projects";
import JobProgressIndicator from "@components/JobProgressIndicator/JobProgressIndicator";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Card, CardContent, CardHeader, Divider, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { observer } from "mobx-react";
import { useParams } from "react-router-dom";

export interface JobProgressPanelProps {
  open: boolean;
  onClose?: () => void;
}

// Styled TableCell with vertical divider
const DividedTableCell = styled(TableCell)(({ theme }) => ({
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    right: 0,
    top: "25%",
    height: "50%",
    width: "1px",
    backgroundColor: theme.palette.divider,
  },
}));

// Helper function to format seconds into human-readable time (minutes and hours only)
const formatTime = (seconds: number): string => {
  if (!seconds || seconds <= 0) return "—";

  if (seconds < 60) {
    return "< 1m";
  } else if (seconds < 3600) {
    const minutes = Math.ceil(seconds / 60);
    return `${minutes}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
};

const JobProgressPanel = observer((props: JobProgressPanelProps) => {
  const { open, onClose } = props;
  const { id: projectId } = useParams();
  const appStore = useAppStore();
  const project = useProject(projectId!);
  const appSettings = useAppSettings();

  // Check if webhooks are enabled
  const webhooksEnabled = appSettings.data?.settings?.[AppSettingType.DesignAutomation_EnableWebhooks]?.value?.toLowerCase() === "true";

  // Collect all active jobs from the store
  // Note: No useMemo here - MobX observer will handle reactivity automatically
  const activeJobs: Array<{
    projectId: string;
    modelId: string;
    modelName?: string;
    progress?: number;
    progressType?: string;
    status?: string;
    message?: string;
    elapsedSeconds?: number;
    estimatedRemainingSeconds?: number;
  }> = [];

  Object.keys(appStore.jobStatus.projects).forEach((projectId) => {
    Object.keys(appStore.jobStatus.projects[projectId]).forEach((modelId) => {
      const entry = appStore.jobStatus.projects[projectId][modelId];
      const progress = entry?.progress;
      const jobStatus = entry?.jobStatus;

      // Get model name from jobStatus if available, otherwise lookup from project models
      let modelName: string | undefined;
      if (jobStatus?.job?.model?.apsItemName) {
        modelName = jobStatus.job.model.apsItemName;
      } else if (progress && project.data?.project?.models) {
        const model = project.data.project?.models.find((m: Model) => m.modelId === modelId);
        modelName = model?.apsItemName || modelId;
      } else {
        modelName = modelId;
      }

      // Only include jobs that are in progress or pending
      const isActive = progress !== undefined || jobStatus?.workItemStatus?.status === Status.Inprogress || jobStatus?.workItemStatus?.status === Status.Pending;

      if (isActive) {
        // Compute elapsed seconds if not available from progress
        let elapsedSeconds = progress?.elapsedSeconds;
        if (elapsedSeconds === undefined && jobStatus?.job?.createdAt) {
          const createdAt = new Date(jobStatus.job.createdAt);
          const now = new Date();
          elapsedSeconds = Math.floor((now.getTime() - createdAt.getTime()) / 1000);
        }

        activeJobs.push({
          projectId,
          modelId,
          modelName,
          progress: progress?.progress,
          progressType: progress?.type,
          status: jobStatus?.workItemStatus?.status,
          message: progress?.message,
          elapsedSeconds,
          estimatedRemainingSeconds: progress?.estimatedRemainingSeconds,
        });
      }
    });
  });

  if (!open) {
    return null;
  }

  if (activeJobs.length === 0) {
    return null;
  }

  return (
    <Card
      sx={{
        position: "absolute",
        top: 64, // Below header
        right: 16,
        width: 550,
        maxHeight: 500,
        overflow: "auto",
        zIndex: 1300,
        boxShadow: 3,
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography variant="h6">{i18n.t("job-progress-panel.title")}</Typography>
            </Box>
            {onClose && (
              <IconButton size="small" onClick={onClose} aria-label="close">
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ p: 0 }}>
        <Divider />
        <Table size="small">
          <TableHead>
            <TableRow>
              <DividedTableCell>{i18n.t("job-progress-panel.model")}</DividedTableCell>
              <DividedTableCell align="right">{i18n.t("job-progress-panel.elapsed")}</DividedTableCell>
              {webhooksEnabled && <DividedTableCell align="right">{i18n.t("job-progress-panel.remaining")}</DividedTableCell>}
              <TableCell align="center"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeJobs.map((job) => (
              <TableRow key={`${job.projectId}-${job.modelId}`} hover>
                <TableCell>
                  <Box>
                    <Typography variant="body2" noWrap>
                      {job.modelName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {webhooksEnabled
                        ? job.progress !== undefined && job.progress > 0
                          ? `${job.progress}% - ${job.message || i18n.t("job-progress-panel.processing")}`
                          : i18n.t("job-progress-panel.starting")
                        : i18n.t(`design-automation.status.${job.status}`)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption">{job.elapsedSeconds !== undefined && job.elapsedSeconds > 0 ? formatTime(job.elapsedSeconds) : "—"}</Typography>
                </TableCell>
                {webhooksEnabled && (
                  <TableCell align="right">
                    <Typography variant="caption">
                      {job.estimatedRemainingSeconds !== undefined && job.estimatedRemainingSeconds > 0 ? `~${formatTime(job.estimatedRemainingSeconds)}` : "—"}
                    </Typography>
                  </TableCell>
                )}
                <TableCell align="center">
                  <JobProgressIndicator progress={job.progress} progressType={job.progressType} status={job.status as any} size={32} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
});

export default JobProgressPanel;
